import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { SECTIONS } from 'components/upb_viewer/constants';
import { constructUrlPathSegment } from 'components/upb_viewer/UPBViewer';
import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
} from 'components/upb_viewer_beta/css/betaTokens';
import BetaPdpSection from 'components/upb_viewer_beta/pdp/BetaPdpSection';
import { DASH, fmtInt, fmtMoney } from 'components/upb_viewer_beta/pdp/format';
import useGet from 'lib/hooks/useGet';
import { GLOBAL_PRODUCT_DISTRIBUTORS } from 'lib/networking/endpoints';

const BETA_BASE = '/upb-viewer-beta';

// "L3 Category" -> 3 (fallback when the backend payload predates the `level` key).
function parseLevel(displayName) {
  const match = /L(\d+)/.exec(displayName || '');
  return match ? Number(match[1]) : null;
}

/**
 * Drill-down URL for a taxonomy level, mirroring how BetaCategoryPage builds its
 * navigation: each segment is `name__id` via constructUrlPathSegment, and the
 * path is cumulative (L2 needs L1+L2 segments, L3 needs L1+L2+L3, …).
 *
 *   L0  -> the catalog root (the viewer does not drill L0)
 *   L1..L4 -> /upb-viewer-beta/{l1seg}/…/{levelseg}  (L4 lands on the products page)
 *
 * Returns null when any required parent id/name is missing, so the row degrades
 * to plain (non-clickable) text rather than a broken link.
 */
function buildLevelUrl(rowsByLevel, level) {
  if (level == null) return null;
  if (level <= 0) return BETA_BASE;
  const segments = [];
  for (let lvl = 1; lvl <= level; lvl += 1) {
    const row = rowsByLevel[lvl];
    if (!row || row.categoryId == null || !row.name) return null;
    const seg = constructUrlPathSegment({ value: row.categoryId, label: row.name });
    if (!seg) return null;
    segments.push(seg);
  }
  return `${BETA_BASE}/${segments.join('/')}`;
}

function TaxonomiesSection({ product }) {
  const section = (product?.sections || []).find(s => s.name === SECTIONS.TAXONOMIES);

  const rows = useMemo(
    () =>
      (section?.attributes || [])
        .map(attr => ({
          level: typeof attr.level === 'number' ? attr.level : parseLevel(attr.display_name),
          label: attr.display_name,
          name: attr.attribute_value,
          categoryId: attr.category_id,
        }))
        .filter(r => r.level != null)
        .sort((a, b) => a.level - b.level),
    [section],
  );

  const rowsByLevel = useMemo(
    () =>
      rows.reduce((acc, r) => {
        acc[r.level] = r;
        return acc;
      }, {}),
    [rows],
  );

  return (
    <BetaPdpSection title="Taxonomies">
      {rows.length === 0 ? (
        <div className={css(styles.empty)}>
          This product is unclassified — no taxonomy path to show.
        </div>
      ) : (
        <Table className={css(styles.table)}>
          <thead>
            <tr>
              <th className={css(styles.th, styles.thLevel)}>Level</th>
              <th className={css(styles.th)}>Category</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const url = buildLevelUrl(rowsByLevel, r.level);
              return (
                <tr key={r.level} className={css(idx % 2 === 1 && styles.rowAlt)}>
                  <td className={css(styles.td, styles.levelCell)}>{r.label}</td>
                  <td className={css(styles.td)}>
                    {!r.name ? (
                      <span className={css(styles.muted)}>{DASH}</span>
                    ) : url ? (
                      <Link to={url} className={css(styles.link)}>
                        {r.name}
                      </Link>
                    ) : (
                      <span className={css(styles.valueText)}>{r.name}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </BetaPdpSection>
  );
}
TaxonomiesSection.propTypes = {
  product: PropTypes.shape({ sections: PropTypes.array }),
};
TaxonomiesSection.defaultProps = { product: {} };

function DistributorsSection({ productId }) {
  const { data: resp = {}, loading } = useGet(
    `${GLOBAL_PRODUCT_DISTRIBUTORS}/${productId}`,
    {},
    !productId,
  );
  const distributors = resp.data || [];

  return (
    <BetaPdpSection title="Distributors Carrying This Product">
      {loading ? (
        <div className={css(styles.empty)}>Loading distributors…</div>
      ) : distributors.length === 0 ? (
        <div className={css(styles.empty)}>
          No distributors are currently carrying this product.
        </div>
      ) : (
        <Table className={css(styles.table)}>
          <thead>
            <tr>
              <th className={css(styles.th)}>Distributor</th>
              <th className={css(styles.th)}>Product name</th>
              <th className={css(styles.th)}>SKU</th>
              <th className={css(styles.th, styles.thNum)}>Annualized GMV</th>
              <th className={css(styles.th, styles.thNum)}>Units sold</th>
            </tr>
          </thead>
          <tbody>
            {distributors.map((d, idx) => (
              <tr key={`${d.sku || ''}-${idx}`} className={css(idx % 2 === 1 && styles.rowAlt)}>
                <td className={css(styles.td, styles.distributorCell)}>
                  {d.distributor_name || DASH}
                </td>
                <td className={css(styles.td, styles.bodyCell)}>{d.product_name || DASH}</td>
                <td className={css(styles.td, styles.bodyCell)}>{d.sku || DASH}</td>
                <td className={css(styles.td, styles.numCell)}>{fmtMoney(d.annualized_gmv)}</td>
                <td className={css(styles.td, styles.numCell)}>{fmtInt(d.annualized_units)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </BetaPdpSection>
  );
}
DistributorsSection.propTypes = { productId: PropTypes.string };
DistributorsSection.defaultProps = { productId: null };

function BetaTaxonomyTab({ product, productId }) {
  return (
    <div className={css(styles.wrap)}>
      <TaxonomiesSection product={product} />
      <DistributorsSection productId={productId} />
    </div>
  );
}

BetaTaxonomyTab.propTypes = {
  product: PropTypes.shape({ sections: PropTypes.array }),
  productId: PropTypes.string,
};

BetaTaxonomyTab.defaultProps = {
  product: {},
  productId: null,
};

const styles = StyleSheet.create({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    fontFamily: FONT_BODY,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    marginTop: 12,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  th: {
    textAlign: 'left',
    fontFamily: FONT_BODY,
    fontSize: 11,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '12px 18px',
    borderBottom: `1px solid ${BORDER_2}`,
    backgroundColor: BG_2,
    whiteSpace: 'nowrap',
  },
  thLevel: {
    width: 220,
  },
  thNum: {
    textAlign: 'right',
  },
  rowAlt: {
    backgroundColor: BG_2,
  },
  td: {
    fontFamily: FONT_BODY,
    fontSize: 14,
    padding: '14px 18px',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${BORDER_2}`,
  },
  levelCell: {
    color: FG_3,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
  },
  distributorCell: {
    color: FG_1,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    whiteSpace: 'nowrap',
  },
  bodyCell: {
    color: FG_2,
  },
  numCell: {
    textAlign: 'right',
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  valueText: {
    color: FG_1,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
  },
  link: {
    color: PRIMARY_COLOR,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      color: PRIMARY_HOVER,
      textDecoration: 'underline',
    },
    ':focus-visible': {
      color: PRIMARY_HOVER,
      outline: 'none',
      textDecoration: 'underline',
    },
  },
  muted: {
    color: FG_3,
  },
  empty: {
    fontFamily: FONT_BODY,
    fontSize: 14,
    color: FG_3,
    padding: '16px 4px',
  },
});

export default BetaTaxonomyTab;
